import { create } from "zustand";
import {
  fetchAndActivate,
  getBoolean,
  getRemoteConfig,
} from "firebase/remote-config";
import { getFirebaseApp } from "@/lib/firebase";
import {
  DEFAULT_FLAGS,
  FeatureFlag,
  type FeatureFlags,
} from "../types/featureFlags.types";

const FETCH_INTERVAL_MS = 5 * 60 * 1000;
const FETCH_TIMEOUT_MS = 3000;

/** Everything off until Remote Config settles, so gated UI never flashes on then vanishes. */
const PENDING_FLAGS = Object.fromEntries(
  Object.values(FeatureFlag).map((flag) => [flag, false]),
) as FeatureFlags;

interface FeatureFlagState {
  flags: FeatureFlags;
  /** False until the first Remote Config fetch settles (success or failure). */
  ready: boolean;
  init: () => Promise<void>;
}

export const useFeatureFlagStore = create<FeatureFlagState>((set, get) => ({
  flags: PENDING_FLAGS,
  ready: false,
  init: async () => {
    if (get().ready) return;
    let flags = DEFAULT_FLAGS;
    try {
      const app = getFirebaseApp();
      if (!app) return;
      const rc = getRemoteConfig(app);
      rc.defaultConfig = { ...DEFAULT_FLAGS };
      rc.settings.minimumFetchIntervalMillis = import.meta.env.DEV
        ? 0
        : FETCH_INTERVAL_MS;
      rc.settings.fetchTimeoutMillis = FETCH_TIMEOUT_MS;
      await fetchAndActivate(rc);
      flags = { ...DEFAULT_FLAGS };
      for (const flag of Object.values(FeatureFlag)) {
        flags[flag] = getBoolean(rc, flag);
      }
    } catch {
      // Remote Config unreachable: keep defaults so the app still works
    } finally {
      set({ flags, ready: true });
    }
  },
}));

export function useFeatureFlag(flag: FeatureFlag): boolean {
  return useFeatureFlagStore((s) => s.flags[flag]);
}
